/**
 * AmbientCull — pure decision helpers for camera-distance ambient particle culling.
 *
 * When the camera is zoomed far out (cinematic moments, level-end reveals)
 * ambient particle systems become invisible noise at the scale of the frame.
 * Culling their emitRate to 0 saves GPU particle budget at exactly the
 * moments when the effect would be least noticeable.
 *
 * The exported helpers are PURE functions with no side effects so they can be
 * unit-tested without a Babylon scene. CameraSystem.update() applies the
 * decision to the registered particle systems, guarded by the !qaActive gate.
 */

/**
 * Returns `true` when the ambient particle systems should be culled
 * (emitRate → 0) and `false` when they should be restored.
 *
 * @param distance        Current camera Z distance (world units).
 * @param threshold       Distance above which we start culling.
 * @param hysteresis      Dead-band subtracted from threshold for restore:
 *                        restore only fires when distance < threshold - hysteresis.
 *                        Must be >= 0.
 * @param currentlyCulled Whether culling is already active this frame.
 *
 * State machine (no-flap hysteresis):
 *   - distance >= threshold           → cull (regardless of current state)
 *   - distance < threshold - hysteresis → restore
 *   - else (in the band)              → no change (preserve currentlyCulled)
 */
export function shouldCullAmbient(
  distance: number,
  threshold: number,
  hysteresis: number,
  currentlyCulled: boolean,
): boolean {
  if (distance >= threshold) {
    return true;
  }
  if (distance < threshold - hysteresis) {
    return false;
  }
  // In the hysteresis band: no change.
  return currentlyCulled;
}
